import { Alert, Button, Icon, List, ListRow, Section, Spinner, Stack } from '@ui';
import type { TFunction } from 'i18next';
import type de from '../../../i18n/locales/de.json';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { PageActions } from '../../../app/PageActions';
import { errorMessage } from '../../../lib/errors';
import { usePermissionCatalog, useRoles, type PermissionGroup, type Role } from '../api';
import { accessSummary, type PermissionRow } from '../permissions';
import { useSession } from '../session';

type Texts = (typeof de)['permissions'];
type AreaKey = keyof Texts['areas'];
type GroupKey = keyof Texts['groups'];
type LabelKey = {
  [A in keyof Texts['labels']]: `${A & string}.${keyof Texts['labels'][A] & string}`;
}[keyof Texts['labels']];

// Keys come from the server's catalog; a missing text shows the key itself.

/** Label of a single permission, e.g. `colonies.write` → „Völker bearbeiten“. */
export function permissionLabel(t: TFunction, key: string): string {
  return t(`permissions.labels.${key as LabelKey}`);
}

/** Heading of a catalog group, e.g. `apiary` → „Imkerei-Betrieb“. */
export function groupLabel(t: TFunction, key: string): string {
  return t(`permissions.groups.${key as GroupKey}`);
}

/** Label of a matrix row: an area (`Völker`) or a single permission. */
export function rowLabel(t: TFunction, row: PermissionRow): string {
  return row.area ? t(`permissions.areas.${row.key as AreaKey}`) : permissionLabel(t, row.key);
}

/** The roles of the business (USR-009); owners can create and change them. */
export function RolesPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { me, can } = useSession();
  const roles = useRoles();
  const catalog = usePermissionCatalog();
  const error = roles.error ?? catalog.error;

  return (
    <Stack gap={6}>
      {can('roles.manage') && (
        <PageActions>
          <Button
            iconStart={<Icon name="plus" size="sm" />}
            onClick={() => navigate('/einstellungen/rollen/neu')}
          >
            {t('roles.create')}
          </Button>
        </PageActions>
      )}
      {error && <Alert tone="danger">{errorMessage(error)}</Alert>}
      {(roles.isPending || catalog.isPending) && !error && <Spinner label={t('common.loading')} />}
      {roles.data && catalog.data && (
        <Section title={t('roles.title')}>
          <List>
            {roles.data.map((role) => (
              <RoleRow
                key={role.id}
                role={role}
                catalog={catalog.data}
                mine={role.id === me.role.id}
              />
            ))}
          </List>
        </Section>
      )}
    </Stack>
  );
}

/** A role with what it may do; the whole row opens the editor. */
function RoleRow({
  role,
  catalog,
  mine,
}: {
  role: Role;
  catalog: PermissionGroup[];
  mine: boolean;
}) {
  const { t } = useTranslation();
  const summary = accessSummary(role.permissions, catalog);
  const list = (rows: PermissionRow[]) => rows.map((r) => rowLabel(t, r)).join(', ');
  const access = role.owner
    ? [t('roles.allPermissions')]
    : role.permissions.length === 0
      ? [t('roles.noPermissions')]
      : [
          summary.write.length > 0 ? `${t('roles.summaryWrite')} ${list(summary.write)}` : null,
          summary.read.length > 0 ? `${t('roles.summaryRead')} ${list(summary.read)}` : null,
        ];

  return (
    <ListRow
      title={role.name}
      href={`/einstellungen/rollen/${role.id}`}
      description={[mine ? t('roles.yours') : null, ...access].filter(Boolean).join(' · ')}
      trailing={t('roles.memberCount', { count: role.member_count })}
    />
  );
}
